import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent } from "@/components/ui/baseUi/dialog"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { UserCell, UserCellSkeleton } from "@/components/ui/layout/table/userCell"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { type FieldValueChange } from "@/typings/formik"
import SearchEmpty from "@images/empty/session_empty.svg"
import { BoxStaffItemEntity } from "@modules/boxStaff/entities/boxStaffEntity"
import { BoxStaffStatus } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffs } from "@modules/boxStaff/hooks/useBoxStaff"
import { word } from "@modules/core/utils/i18n"
import useDebounce from "@modules/hooks/useDebounce"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import { type FieldInputProps } from "formik"
import { Search, X } from "lucide-react"
import { Suspense, useCallback, useImperativeHandle, useRef, useState } from "react"

type AssignedCoachInputProps<T, K> = {
  label: string
  name: string
  textDescription?: string
  isRequiredInput: boolean
  setFieldValue: FieldValueChange<K>
  getFieldProps: (nameOrOptions: T) => FieldInputProps<any>
  error?: React.JSX.Element
} & React.ComponentProps<"div">

type AssignedCoachModalRef = {
  open: () => void
}

export function AssignedCoachInput<T, K>({
  label,
  name,
  isRequiredInput,
  setFieldValue,
  getFieldProps,
  textDescription,
  error,
  ...props
}: AssignedCoachInputProps<T, K>): React.JSX.Element {
  const assignedCoachModal = useRef<AssignedCoachModalRef>(null)
  const coachsSelected: UserEntity[] = (getFieldProps(name as T).value as UserEntity[]) || []

  const handleRemove = useCallback(
    (coach: UserEntity) => {
      setFieldValue(
        name,
        coachsSelected.filter((c: UserEntity) => c.id !== coach.id),
      )
    },
    [coachsSelected, name, setFieldValue],
  )

  return (
    <>
      <InputContainer isRequiredInput={isRequiredInput} label={label} name={name} error={error} {...props}>
        <div className="flex flex-col gap-4 rounded-xl bg-neutral-50 p-6">
          {textDescription && (
            <Text fontStyle="BM" className="text-textDefault-body">
              {textDescription}
            </Text>
          )}

          <Button
            variant="outline"
            size="lg"
            onClick={() => assignedCoachModal.current?.open()}
            className="w-full bg-neutral-50"
          >
            {word("box.slotTemplateGroup.form.assignedStaffMembers.buttonAdd")}
            <Search size={16} color="currentColor" className="text-textDefault-body" />
          </Button>
          {coachsSelected.length > 0 &&
            coachsSelected.map((coach: UserEntity) => (
              <div key={coach.id} className="flex justify-between">
                <UserCell
                  member={{
                    avatar: coach.avatar ?? undefined,
                    firstname: coach.firstname,
                    lastname: coach.lastname,
                  }}
                  subtitle={"@" + coach.username}
                  avatarSize="size-13"
                />
                <div className="flex items-center justify-center gap-4">
                  <X
                    size={22}
                    color="currentColor"
                    className="cursor-pointer text-textDefault-body"
                    onClick={() => handleRemove(coach)}
                  />
                </div>
              </div>
            ))}
        </div>
      </InputContainer>
      <AssignedCoachModal
        ref={assignedCoachModal}
        coachsSelected={coachsSelected}
        setFieldValue={setFieldValue}
        name={name}
      />
    </>
  )
}

function AssignedCoachModal({
  coachsSelected,
  setFieldValue,
  name,
  ref,
}: {
  coachsSelected: UserEntity[]
  setFieldValue: FieldValueChange<any>
  name: string
  ref: React.Ref<AssignedCoachModalRef>
}) {
  const [showModal, setShowModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const onHide = useCallback(() => {
    setSearchTerm("")
    setShowModal(false)
  }, [])

  const clearSearch = () => {
    setSearchTerm("")
  }

  const handleClick = (coach: BoxStaffItemEntity) => {
    setFieldValue(name, [...coachsSelected, coach.convertToUser()])
    setShowModal(false)
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  return (
    <Dialog
      open={showModal}
      onOpenChange={(open) => {
        if (!open) onHide()
      }}
    >
      <DialogContent className="h-[650px] w-full max-w-[512px]">
        <div className="flex h-full w-full flex-col">
          {/* TITLE */}
          <div className="flex flex-col flex-wrap content-center justify-center text-center">
            <Text fontStyle="TL" className="text-textDefault-body">
              {word("box.slotTemplateGroup.form.assignedStaffMembers.buttonAdd")}
            </Text>
          </div>
          {/* SEARCH */}
          <div className="mt-8 flex w-full flex-col gap-4">
            <InputGroup>
              <InputGroupInput
                placeholder={word("searchUserInput.modal.placeholder")}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                value={searchTerm}
              />
              <InputGroupAddon align="inline-start">
                <Search />
              </InputGroupAddon>
              {searchTerm && (
                <InputGroupAddon align="inline-end">
                  <X className="size-4 cursor-pointer" onClick={clearSearch} />
                </InputGroupAddon>
              )}
            </InputGroup>
          </div>
          {/* SEARCH RESULTS */}
          <div className="flex h-full w-full flex-col">
            <Suspense fallback={<TemplateUserCellSkeleton />}>
              <BoxStaffList
                searchTerms={debouncedSearchTerm}
                coachsSelected={coachsSelected}
                handleClick={handleClick}
              />
            </Suspense>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function BoxStaffList({
  searchTerms,
  coachsSelected,
  handleClick,
  className,
  ...props
}: {
  searchTerms?: string
  coachsSelected: UserEntity[]
  handleClick: (coach: BoxStaffItemEntity) => void
} & React.ComponentProps<"div">) {
  const { data } = useBoxStaffs({ nameFilter: searchTerms, pageSize: 50, litsOfStatus: [BoxStaffStatus.ACCEPTED] })
  const coachsFiltered = data.filter((coach) => !coachsSelected.some((selected) => selected.id === coach.userId))

  return (
    <div className={cn("h-full", className)} {...props}>
      {coachsFiltered.length > 0 ? (
        <div className="mt-6 mb-6 flex flex-col gap-2">
          {coachsFiltered.map(
            (person) =>
              person.id &&
              person.firstname &&
              person.lastname && (
                <UserCell
                  key={person.id}
                  member={{
                    avatar: person.avatar,
                    username: person.username,
                    firstname: person.firstname,
                    lastname: person.lastname,
                  }}
                  subtitle={"@" + person.username}
                  avatarSize="size-13"
                  className="flex cursor-pointer gap-2"
                  onClick={() => handleClick(person)}
                />
              ),
          )}
        </div>
      ) : (
        <div className="animate-fadein animate-duration-500 flex h-full w-full flex-col items-center justify-center gap-6">
          <Text fontStyle="LL" className="text-textDefault-body">
            {word("searchUserInput.modal.emptyViewtext")}
          </Text>
          <div
            className="flex h-40 w-full bg-contain bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${SearchEmpty}')` }}
          ></div>
        </div>
      )}
    </div>
  )
}

type TemplateUserCellSkeletonProps = {
  className?: string
} & React.ComponentProps<"div">

export function TemplateUserCellSkeleton({ className, ...props }: TemplateUserCellSkeletonProps) {
  return (
    <div className={cn("my-6 flex animate-in flex-col gap-2 duration-1000 fade-in", className)} {...props}>
      <UserCellSkeleton subtitle={true} avatarSize="size-13" />
      <UserCellSkeleton subtitle={true} avatarSize="size-13" />
      <UserCellSkeleton subtitle={true} avatarSize="size-13" />
      <UserCellSkeleton subtitle={true} avatarSize="size-13" />
      <UserCellSkeleton subtitle={true} avatarSize="size-13" />
    </div>
  )
}
